import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({ hoje: 0, atrasados: 0, urgentes: 0 });
  const [items, setItems] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reminders/stats`);
      setStats(res.data || {});
    } catch (e) {
      // ignore
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reminders?limit=20`);
      const list = (res.data || []).filter(r => r.status === 'atrasado' || r.status === 'pendente' || r.status === 'em_andamento').slice(0, 8);
      setItems(list);
    } catch (e) {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const t = setInterval(fetchStats, 60000); // refresh a cada 1min
    return () => clearInterval(t);
  }, [fetchStats]);

  useEffect(() => { if (open) fetchItems(); }, [open, fetchItems]);

  const total = (stats.atrasados || 0) + (stats.hoje || 0);
  const isMoreThan99 = total > 99;

  return (
    <div className="relative">
      <button
        className="relative p-2 rounded-full hover:bg-muted transition"
        onClick={() => setOpen(!open)}
        data-testid="notif-bell-btn"
        aria-label="Lembretes"
      >
        <Bell className="h-5 w-5" />
        {total > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 ${stats.atrasados > 0 ? 'bg-red-500' : 'bg-primary'} text-white text-[10px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-bold`}>
            {isMoreThan99 ? '99+' : total}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 w-80 max-w-[calc(100vw-1rem)] bg-background border rounded-lg shadow-xl overflow-hidden">
            <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
              <h3 className="font-semibold text-sm">Lembretes</h3>
              <button className="text-xs text-primary hover:underline" onClick={() => { setOpen(false); navigate('/lembretes'); }} data-testid="notif-ver-todos">Ver todos</button>
            </div>
            <div className="grid grid-cols-3 gap-1 p-2 text-center text-xs border-b">
              <button className={`p-2 rounded ${stats.atrasados > 0 ? 'bg-red-500/10 text-red-600' : 'text-muted-foreground'}`} onClick={() => { setOpen(false); navigate('/lembretes'); }}>
                <div className="font-bold text-base">{stats.atrasados || 0}</div>
                <div>Atrasados</div>
              </button>
              <button className="p-2 rounded bg-blue-500/10 text-blue-600" onClick={() => { setOpen(false); navigate('/lembretes'); }}>
                <div className="font-bold text-base">{stats.hoje || 0}</div>
                <div>Hoje</div>
              </button>
              <button className="p-2 rounded bg-orange-500/10 text-orange-600" onClick={() => { setOpen(false); navigate('/lembretes'); }}>
                <div className="font-bold text-base">{stats.urgentes || 0}</div>
                <div>Urgentes</div>
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {items.length === 0 && <div className="text-center text-muted-foreground text-sm py-6">Nenhum lembrete ativo</div>}
              {items.map(r => (
                <button key={r.id} onClick={() => { setOpen(false); navigate('/lembretes'); }} className="w-full text-left px-3 py-2 border-b hover:bg-muted text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`w-1 self-stretch min-h-[24px] rounded ${r.status === 'atrasado' ? 'bg-red-500' : r.prioridade === 'urgente' ? 'bg-red-500' : r.prioridade === 'alta' ? 'bg-orange-500' : 'bg-blue-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.titulo}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.data ? String(r.data).split('T')[0].split('-').reverse().join('/') : 'Sem data'}
                        {r.status === 'atrasado' && <span className="ml-2 text-red-500 font-semibold">Atrasado</span>}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
